import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Typography, Select, message } from 'antd';
import {
	downloadLocationsCSVForSupplier,
	fetchLocationByIdForSupplier,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { updateFiltersEntities } from '../../thunks/thunks_wrapper';
import { LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/locations_actions';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { EmptyState } from '../empty_state/EmptyState';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getTupleNameFieldName,
	getTupleValueFieldName,
	isSupplierPayingCustomerAndAdmin,
} from '../../utils/DataAccessUtils';
import { EmptorTag } from '../emptor_tag/EmptorTag';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWAsyncTupleSelect from '../ow_async_select/OWAsyncTupleSelect';
import { locationTypesAdditionalFieldsRestCrudThunksForSupplier } from '../../thunks/location_types_additional_fields_thunks';
import { SUPPLIER_ADMIN_ONLY } from '../roles/roles';
import { dispatchersRestCrudThunksForSupplier } from '../../thunks/dispatchers_thunks';
import { INITIAL_IS_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import LocationsReduxDataTable from '../locations_redux_data_table/LocationsReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { DownloadOutlined } from '@ant-design/icons';
import BrandLogoDisplay from '../brands_overview_index_page/BrandLogoDisplay';
import { useFilters } from '../../hooks/useFilters';
import {
	getLocationFilterConfig,
	syncLocationsOverviewQueryParams,
} from '../../utils/LocationFilterUtils';
import {
	buildLocationsMapPopupParts,
	getLocationContactGroups,
} from '../../utils/LocationsMapPopupUtils';
import LocationsViewModeToggle, {
	LocationsViewMode,
} from '../locations_view_mode_toggle/LocationsViewModeToggle';
import LocationsMapView from '../locations_map_view/LocationsMapView';
import LocationsOverviewMapToolbar from '../locations_overview_map_toolbar/LocationsOverviewMapToolbar';

require('./SupplierLocationsIndexPage.less');
const { Text } = Typography;

const TC_NAME = 'locationsIndex';
const LOCATIONS_OVERVIEW_TARGET_COLLECTION = 'locationsIndex';
const LOCATIONS_ADVANCED_FILTERS = 'locationsAdvancedFilters';

const SupplierLocationsIndexPage: FC<any> = ({
	history,
	location,
	locations,
	setLocationFiltersInStore,
	refetchLocationsWithFilters,
	clearAndUpdateFilters,
	currentUser,
	exportLocations,
	fetchLocationTypesAdditionalFields,
	fetchMultipleLocationTypesAdditionalFields,
	locationTypesAdditionalFields,
	fetchFilteredLocationsLite,
	fetchLocationDetails,
	fetchDispatchersForLocation,
}) => {
	const [exporting, setExporting] = useState(false);
	const [viewMode, setViewMode] = useState<LocationsViewMode>('list');
	const [inlineMapLoading, setInlineMapLoading] = useState(false);
	const [inlineMapLocations, setInlineMapLocations] = useState<any[]>([]);
	const [mapFullscreenOpen, setMapFullscreenOpen] = useState(false);

	const activeFilters = useMemo(
		() => nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {}),
		[locations]
	);

	// Holds the live merged filter state for map mode. `activeFilters` is memoized off the
	// `locations` prop, so within a single render tick it is the pre-render snapshot. When
	// FilterOptionsWrapper switches an operator it fires handleChange immediately followed by
	// updateFilters({}); both run before a re-render, so merging the second call against the
	// stale `activeFilters` would re-issue the map request with the old filter values. Merging
	// against this ref instead keeps the follow-up call in sync with what was just applied.
	// Synced to the latest committed filters on every render (no effect needed).
	const latestMapFiltersRef = useRef(activeFilters);
	latestMapFiltersRef.current = activeFilters;

	const loadInlineMapLocations = useCallback(
		(filters) => {
			setInlineMapLoading(true);
			return fetchFilteredLocationsLite(filters)
				.then((data) => {
					setInlineMapLocations(Array.isArray(data) ? data : []);
				})
				.catch((err) => {
					message.error(typeof err === 'string' ? err : 'Failed to load locations');
					setViewMode('list');
					setMapFullscreenOpen(false);
				})
				.finally(() => setInlineMapLoading(false));
		},
		[fetchFilteredLocationsLite]
	);

	const applyLocationFilters = useCallback(
		(filters) => {
			setLocationFiltersInStore(filters);
			if (viewMode === 'list') {
				refetchLocationsWithFilters(filters);
			} else if (viewMode === 'map') {
				const mergedFilters = { ...latestMapFiltersRef.current, ...filters };
				latestMapFiltersRef.current = mergedFilters;
				syncLocationsOverviewQueryParams(history, locations, mergedFilters, TC_NAME);
				loadInlineMapLocations(mergedFilters);
			}
		},
		[
			setLocationFiltersInStore,
			refetchLocationsWithFilters,
			loadInlineMapLocations,
			viewMode,
			history,
			locations,
		]
	);

	const applyClearAndUpdateFilters = useCallback(
		(filters, targetCollectionName) => {
			clearAndUpdateFilters(filters, targetCollectionName);
			if (viewMode === 'list') {
				refetchLocationsWithFilters({});
			} else if (viewMode === 'map') {
				latestMapFiltersRef.current = filters;
				syncLocationsOverviewQueryParams(history, locations, filters, TC_NAME);
				loadInlineMapLocations(filters);
			}
		},
		[
			clearAndUpdateFilters,
			viewMode,
			refetchLocationsWithFilters,
			loadInlineMapLocations,
			history,
			locations,
		]
	);

	const locationFilters = useFilters({
		filterTargetCollectionName: LOCATIONS_ADVANCED_FILTERS,
		overviewCollectionName: LOCATIONS_OVERVIEW_TARGET_COLLECTION,
		updateFilters: applyLocationFilters,
	});

	const additionalFieldsFilter = useMemo(
		() => ({
			label: 'Additional Fields',
			fieldName: FILTER_CONFIG_NAMES.ADDITIONAL_FIELD,
			type: FILTER_FIELD_TYPE.TUPLE,
			stateSlice: locationTypesAdditionalFields,
			filtersValueAccessor: (filtersStateSlice) => {
				const name = filtersStateSlice[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				const value =
					filtersStateSlice[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				return [name, value];
			},
			handleChange: (values) => {
				applyLocationFilters({
					[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'0',
						values,
						undefined
					),
					[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'1',
						values,
						undefined
					),
				});
			},
			targetCollectionName: LOCATIONS_ADVANCED_FILTERS,
			fetch: fetchLocationTypesAdditionalFields,
			fetchMultiple: fetchMultipleLocationTypesAdditionalFields,
			renderRecord: (entity) => (
				<Select.Option key={entity.id} value={entity.id}>
					<div className="flex flex-row items-center">
						<div>{entity.name}</div>
					</div>
				</Select.Option>
			),
			valueAccessor: (item) => item.name,
			component: OWAsyncTupleSelect,
		}),
		[
			locationTypesAdditionalFields,
			applyLocationFilters,
			fetchLocationTypesAdditionalFields,
			fetchMultipleLocationTypesAdditionalFields,
		]
	);

	const filterConfig = useMemo(
		() =>
			getLocationFilterConfig({
				filters: locationFilters,
				additionalFieldsFilter,
				showCustomers: isSupplierPayingCustomerAndAdmin(currentUser),
			}),
		[locationFilters, additionalFieldsFilter, currentUser]
	);

	const downloadCSV = useCallback(() => {
		const filters = nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {});
		setExporting(true);
		exportLocations(filters)
			.catch((err) => message.error(err))
			.finally(() => setExporting(false));
	}, [locations, exportLocations]);

	// Internal-tech suppliers (private network) self-perform on the buyer's locations, so they also
	// see the assigned field techs; third-party suppliers see only the primary + facilities contacts.
	const showTechnicians = isInternalTech(currentUser);

	const popupRenderFunc = useCallback(
		(record) => buildLocationsMapPopupParts(record, ROLE_TYPES.SUPPLIER, { showTechnicians }),
		[showTechnicians]
	);

	const mapContactGroupsAccessor = useCallback(
		(record) => getLocationContactGroups(record, { showTechnicians }),
		[showTechnicians]
	);

	const fetchLocationDetailsForMap = useCallback(
		(entity) => {
			const locationId = nullSafeGet('id', entity);
			if (!showTechnicians) {
				return fetchLocationDetails(locationId);
			}
			return Promise.all([
				fetchLocationDetails(locationId),
				// The location detail response has no primary techs — those live on the per-location
				// supplier dispatcher — so fetch it separately and merge, leaving both thunks untouched.
				fetchDispatchersForLocation(locationId).catch(() => []),
			]).then(([location, dispatchers]) => {
				// Match the dispatcher to this location by locationId rather than assuming the first
				// element (the list endpoint can return every location's dispatcher for the facility).
				const dispatcherSeq = Array.isArray(dispatchers) ? dispatchers : [dispatchers];
				const dispatcher = dispatcherSeq.find(
					(candidate) =>
						candidate && String(nullSafeGet('locationId', candidate)) === String(locationId)
				);
				const base = location || entity;
				if (!dispatcher) {
					return base;
				}
				return {
					...base,
					primaryTechList: nullSafeGetOrElse('primaryTechList', dispatcher, []),
					primaryTechListContacts: nullSafeGetOrElse('primaryTechListContacts', dispatcher, []),
				};
			});
		},
		[fetchLocationDetails, fetchDispatchersForLocation, showTechnicians]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				defaultSortOrder: 'ascend',
				render: (text, record) => (
					<div className="locationsIndexPage__row">
						<div>
							<div className="flex items-center">
								<BrandLogoDisplay brand={nullSafeGet('brand', record)} />
								<div>
									<div className="locationsIndexPage__rowTitle">
										<Ellipsis tooltip={true} length={64}>
											{record.name}
										</Ellipsis>
									</div>
									<div className="locationsIndexPage__rowSubtitle">
										<div className="locationsIndexPage__rowInlineGroup">
											{nullSafeGet('buyerFacility.primaryAddress.city', record)}
										</div>
										<div className="locationsIndexPage__rowInlineGroup2">
											{nullSafeGet('locationType.name', record)} {nullSafeGet('storeId', record)}
											{isSupplierPayingCustomerAndAdmin(currentUser) ? (
												<span className="ml-2">
													| {nullSafeGet('buyerCompany.displayName', record)}
												</span>
											) : null}
										</div>
										{record.isActive ? null : (
											<div className="locationsIndexPage__rowInlineGroup">
												<Text className={'mr-1'} type={'danger'}>
													Inactive
												</Text>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="locationsIndexPage__rightSide">
							<EmptorTag className="locationsIndexPage__pill">
								{record.totalNotClosedWOCount > 0
									? record.totalNotClosedWOCount + ' Open WOs'
									: 'No Open WOs'}
							</EmptorTag>
						</div>
					</div>
				),
			},
		],
		[currentUser]
	);

	const detailPathForRecord = useCallback(
		(record) => {
			const path = `/supplier/locations/detail/${nullSafeGet(
				'id',
				record
			)}/details?targetCollectionName=${LOCATIONS_OVERVIEW_TARGET_COLLECTION}`;
			return getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
				preserve: true,
			});
		},
		[location]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => history.push(detailPathForRecord(record)),
		}),
		[history, detailPathForRecord]
	);

	const isAdmin = currentUser.roles.some((role) => SUPPLIER_ADMIN_ONLY.has(role));

	const handleViewModeChange = useCallback(
		(nextViewMode: LocationsViewMode) => {
			if (nextViewMode === viewMode) {
				return;
			}
			setViewMode(nextViewMode);
			if (nextViewMode === 'list') {
				setInlineMapLocations([]);
				setMapFullscreenOpen(false);
				refetchLocationsWithFilters({});
			} else {
				loadInlineMapLocations(activeFilters);
			}
		},
		[viewMode, refetchLocationsWithFilters, loadInlineMapLocations, activeFilters]
	);

	const longitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.longitude', el), []);
	const latitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.latitude', el), []);

	const exportCsvButton = useMemo(
		() => (
			<Button
				size="large"
				icon={<DownloadOutlined />}
				className="inline-block-visible-md"
				loading={exporting}
				onClick={downloadCSV}
			>
				<span className="inline-block-visible-xl">Export CSV</span>
			</Button>
		),
		[downloadCSV, exporting]
	);

	const addLocationButton = useMemo(
		() =>
			isAdmin ? (
				<Button
					size="large"
					type="primary"
					onClick={() => history.push('/supplier/locations/overview/new')}
				>
					Add Location
				</Button>
			) : null,
		[history, isAdmin]
	);

	const locationsTableRightActions = useMemo(
		() => (
			<div className="flex items-center gap-2">
				{exportCsvButton}
				<LocationsViewModeToggle
					viewMode={viewMode}
					onViewModeChange={handleViewModeChange}
					isSupplier={true}
				/>
				{addLocationButton}
			</div>
		),
		[exportCsvButton, addLocationButton, viewMode, handleViewModeChange]
	);

	const locationsFullscreenToolbar = useMemo(
		() => (
			<LocationsOverviewMapToolbar
				filterConfig={filterConfig}
				updateFilters={applyLocationFilters}
				clearAndUpdateFilters={applyClearAndUpdateFilters}
				locations={locations}
				targetCollectionName={TC_NAME}
				rightActions={
					<div className="flex items-center gap-2">
						{exportCsvButton}
						{addLocationButton}
					</div>
				}
			/>
		),
		[
			filterConfig,
			applyLocationFilters,
			applyClearAndUpdateFilters,
			locations,
			exportCsvButton,
			addLocationButton,
		]
	);

	return (
		<div className="locationsIndexPage overflow-x-hidden">
			<LocationsMapView
				fullscreen
				open={mapFullscreenOpen}
				onClose={() => setMapFullscreenOpen(false)}
				locations={inlineMapLocations}
				popupRenderFunc={popupRenderFunc}
				fetchLocationDetails={fetchLocationDetailsForMap}
				longitudeAccessor={longitudeForMap}
				latitudeAccessor={latitudeForMap}
				detailPathForRecord={detailPathForRecord}
				contactGroupsAccessor={mapContactGroupsAccessor}
				toolbar={locationsFullscreenToolbar}
			/>
			<LocationsReduxDataTable
				viewMode={viewMode}
				applyFilters={applyLocationFilters}
				clearAndUpdateFilters={applyClearAndUpdateFilters}
				mapContentLoading={inlineMapLoading}
				mapContent={
					<LocationsMapView
						locations={inlineMapLocations}
						popupRenderFunc={popupRenderFunc}
						fetchLocationDetails={fetchLocationDetailsForMap}
						longitudeAccessor={longitudeForMap}
						latitudeAccessor={latitudeForMap}
						detailPathForRecord={detailPathForRecord}
						onExpand={() => setMapFullscreenOpen(true)}
					/>
				}
				filterConfig={filterConfig}
				rightActions={locationsTableRightActions}
				handleQueryParams
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt={"It's a little lonely in here."}
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any locations yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
									<Button
										type="primary"
										onClick={() => history.push('/supplier/locations/overview/new')}
									>
										Add your first location
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
				entityCollectionName="locations"
				targetCollectionName={TC_NAME}
				tableColumns={columns}
				showHeader={false}
				keyAccessor={(el) => el.id}
				onTableRow={onRow}
				preAppliedFilters={{ ...INITIAL_IS_ACTIVE_FILTERS }}
				fetchData={locationsRestCrudThunksForSupplier}
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
				stateSlice={locations}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	locations: state.locations,
	currentUser: state.session.currentUser,
	locationTypesAdditionalFields: state.location_types_additional_fields,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	setLocationFiltersInStore: (filters) =>
		dispatch(
			LOCATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, LOCATIONS_OVERVIEW_TARGET_COLLECTION)
		),
	refetchLocationsWithFilters: (filters) =>
		dispatch(
			updateFiltersEntities(
				ROLE_TYPES.SUPPLIER,
				'locations',
				locationsRestCrudThunksForSupplier,
				null
			)(true, ownProps.history, LOCATIONS_OVERVIEW_TARGET_COLLECTION, filters, undefined)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(LOCATIONS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	fetchMultipleLocationTypesAdditionalFields: (ids, targetCollectionName) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
		),
	fetchLocationTypesAdditionalFields: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	exportLocations: (params, filters) => dispatch(downloadLocationsCSVForSupplier(params, filters)),
	fetchFilteredLocationsLite: (filters) =>
		dispatch(
			locationsRestCrudThunksForSupplier.readLite(
				{ no_pagination: true },
				'__supplierLocationsInlineMap__',
				null,
				null,
				filters,
				false,
				true
			)
		),
	fetchLocationDetails: (id) => dispatch(fetchLocationByIdForSupplier(id)),
	// Separate, map-only lookup of the location's supplier dispatcher (carries primaryTechList /
	// primaryTechListContacts, which the location detail response does not). Per-location target
	// collection so quick row switches don't take-latest-cancel each other into a hung promise.
	fetchDispatchersForLocation: (locationId) =>
		dispatch(
			dispatchersRestCrudThunksForSupplier.read(
				{ locationId },
				`__supplierMapDispatcher_${locationId}__`
			)
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SupplierLocationsIndexPage));
